/**
 * ActionCable WebSocket Service
 * Manages real-time WebSocket connection to the Rails backend
 */

import { createConsumer, Consumer, Subscription } from '@rails/actioncable';
import AsyncStorage from '@react-native-async-storage/async-storage';
import logger from '../utils/logger';

// WebSocket URL — same host as API but using /cable endpoint
const getWebSocketUrl = () => {
  const apiUrl = process.env.EXPO_PUBLIC_API_URL || (
    __DEV__
      ? 'http://localhost:3000/api/v0'
      : 'https://api-staging.wealfi.co/api/v0'
  );
  // Strip /api/v0 to get base host, then add /cable
  const baseUrl = apiUrl.replace(/\/api\/v0$/, '');
  // Convert http(s) to ws(s)
  return baseUrl.replace(/^http/, 'ws') + '/cable';
};

let consumer: Consumer | null = null;

/**
 * Get or create the ActionCable consumer with auth token
 */
export async function getConsumer(): Promise<Consumer> {
  const token = await AsyncStorage.getItem('access_token');
  if (!token) {
    throw new Error('No access token available for WebSocket connection');
  }

  const wsUrl = `${getWebSocketUrl()}?token=${token}`;

  // Recreate consumer if token changed or doesn't exist
  if (consumer) {
    consumer.disconnect();
  }

  consumer = createConsumer(wsUrl);
  logger.log('[Cable] Consumer created for', getWebSocketUrl());

  return consumer;
}

/**
 * Subscribe to a conversation channel for real-time messages
 */
export async function subscribeToConversation(
  conversationId: string,
  onMessage: (data: any) => void
): Promise<Subscription | null> {
  try {
    const cable = await getConsumer();

    const subscription = cable.subscriptions.create(
      {
        channel: 'ConversationChannel',
        conversation_id: conversationId,
      },
      {
        connected() {
          logger.log('[Cable] Connected to conversation:', conversationId);
        },
        disconnected() {
          logger.log('[Cable] Disconnected from conversation:', conversationId);
        },
        received(data: any) {
          logger.log('[Cable] Received data:', data.type);
          if (data.type === 'new_message' && data.message) {
            onMessage(data.message);
          }
        },
      }
    );

    return subscription;
  } catch (error) {
    logger.error('[Cable] Failed to subscribe to conversation:', error);
    return null;
  }
}

/**
 * Disconnect the WebSocket consumer entirely
 */
export function disconnectCable() {
  if (consumer) {
    logger.log('[Cable] Disconnecting consumer');
    consumer.disconnect();
    consumer = null;
  }
}
